import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import ReactGA from "react-ga4";

import { Col, Icon, Row } from "antd";
import { getImageLink, getBackgroundImageSrc, getImageUrl, getIconUrl } from "../../services/asset";
import { parseVideoEmbedSrc, videoEmbedSrc, isMobileScreen, displayPrice } from "../../services/utils";
import styles from "./indexGallery.scss";
import { IndexCarousel } from "../IndexCarousel";
import { CategoryName, CategoryNameHr, ScreenSize } from "../../models";
import getWidthContext from "../../HOCs/getWidthContext";
import { ImageSizeEnum, VideoService } from "../../models/enums"


const VideoThumbnailAlt = "Video thumbnail";
const FloorPlanAlt = "Floor plan";
const LightboxElementClass = "ril-image-current";
const VideoWrapperId = "VideoWrapperId";

// Categories that show info in gallery
const GALLERY_INFO_CATEGORIES = {
  cars: ['osobni-automobili'],
  realEstate: ['prodaja-stanova', 'prodaja-kuca', 'najam-stanova', 'najam-kuca']
};

// Fields to display per category type (mapped from specialAttributes.field)
const GALLERY_INFO_FIELDS = {
  cars: ['makeYear', 'mileage'],
  realEstate: ['area', 'numberOfRooms']
};

// Gallery navigation icon paths
const GALLERY_FLOORPLAN_ICON = getIconUrl("gallery-floorplan.svg");
const GALLERY_VIDEO_ICON = getIconUrl("youtube-white.svg");

// Format rooms with Croatian suffix (soba/sobe)
const formatRoomsCroatian = (value: string): string => {
  const num = parseInt(value, 10);
  if (isNaN(num)) return value;

  const lastDigit = num % 10;
  const lastTwoDigits = num % 100;

  // 11-14 use "soba"
  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
    return `${num} soba`;
  }
  // 1 uses "soba"
  if (lastDigit === 1) {
    return `${num} soba`;
  }
  // 2, 3, 4 use "sobe"
  if (lastDigit >= 2 && lastDigit <= 4) {
    return `${num} sobe`;
  }
  // Everything else uses "soba"
  return `${num} soba`;
};

const IndexGallery = props => {
  const {
    images = [],
    floorPlans = [],
    videoUrl = "",
    width,
    priceReductionPercentage,
    featuredProperty,
    category = "",
    specialAttributes = [],
    adSummary = null
  } = props;

  const renderBadges = () => {
    const isPersonalCar = category === CategoryName.car || category === CategoryNameHr.car;
    const featuredLabel = isPersonalCar && featuredProperty && featuredProperty.label;
    if (!featuredLabel && !priceReductionPercentage) {
      return null;
    }
    return (
      <div className={styles.badges}>
        {featuredLabel && (
          <span className={styles.featuredProperty}>{featuredLabel}</span>
        )}
        {priceReductionPercentage && (
          <span className={styles.percentage}>{priceReductionPercentage}%</span>
        )}
      </div>
    );
  };
  const isMobile = isMobileScreen(width);
  const isMedium = width <= ScreenSize.large;

  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [videoWrapper, setVideoWrapper] = useState(null);
  const [imagesSrc, setImagesSrc] = useState([]);

  // Calculate indexes for navigation icons
  const galleryIndexes = useMemo(() => {
    const imageCount = images.length;
    const floorPlanCount = floorPlans.length;
    const hasVideo = Boolean(videoUrl);

    return {
      firstImageIndex: imageCount > 0 ? 0 : -1,
      firstFloorPlanIndex: floorPlanCount > 0 ? imageCount : -1,
      videoIndex: hasVideo ? imageCount + floorPlanCount : -1,
      hasImages: imageCount > 0,
      hasFloorPlans: floorPlanCount > 0,
      hasVideo
    };
  }, [images.length, floorPlans.length, videoUrl]);

  // Helper function to get gallery info based on category
  const getGalleryInfo = useCallback(() => {
    if (!category || !specialAttributes || specialAttributes.length === 0) {
      return null;
    }

    let categoryType = null;
    if (GALLERY_INFO_CATEGORIES.cars.includes(category)) {
      categoryType = 'cars';
    } else if (GALLERY_INFO_CATEGORIES.realEstate.includes(category)) {
      categoryType = 'realEstate';
    }

    if (!categoryType) {
      return null;
    }

    const relevantFields = GALLERY_INFO_FIELDS[categoryType];
    const infoParts = [];

    // Add price first
    if (adSummary && adSummary.price) {
      infoParts.push(
        displayPrice(
          adSummary.price,
          adSummary.priceTo,
          {
            rentPriceType: adSummary.rentPriceType,
            priceType: adSummary.priceType
          })
      );
    }

    // Add relevant special attributes
    relevantFields.forEach(field => {
      const attr = specialAttributes.find(a => a.field === field);
      if (attr && attr.value) {
        // Format numberOfRooms with Croatian suffix
        if (field === 'numberOfRooms') {
          infoParts.push(formatRoomsCroatian(attr.value));
        } else if (field === 'makeYear') {
          infoParts.push(`${attr.value}.`);
        } else {
          infoParts.push(attr.value);
        }
      }
    });

    return infoParts.length > 0 ? infoParts.join(" • ") : null;
  }, [category, specialAttributes, adSummary]);

  // Render lightbox navigation icons (for inside the lightbox caption)
  const renderLightboxNavigationIcons = useCallback(() => {
    if (!galleryIndexes.hasFloorPlans && !galleryIndexes.hasVideo) {
      return null;
    }

    return (
      <div className={styles.lightboxNavigation}>
        {galleryIndexes.hasFloorPlans && (
          <button
            type="button"
            className={styles.lightboxNavButton}
            onClick={() => setPhotoIndex(galleryIndexes.firstFloorPlanIndex)}
            title="Tlocrti"
          >
            <img src={GALLERY_FLOORPLAN_ICON} alt="Tlocrti" />
          </button>
        )}
        {galleryIndexes.hasVideo && (
          <button
            type="button"
            className={styles.lightboxNavButton}
            onClick={() => setPhotoIndex(galleryIndexes.videoIndex)}
            title="Video"
          >
            <img src={GALLERY_VIDEO_ICON} alt="Video" />
          </button>
        )}
      </div>
    );
  }, [galleryIndexes]);

  // Build caption with counter and info
  // V2OGLASI-2653: on mobile the prev/next chevrons move to the bottom
  // caption row and sit on either side of a centered counter; the
  // default lightbox vertical-middle arrows are hidden by CSS (mobile
  // only). Desktop layout is unchanged.
  const buildCaption = useCallback((photoIndex: number, totalImages: number) => {
    const counter = `${photoIndex + 1} / ${totalImages}`;
    const galleryInfo = getGalleryInfo();

    if (isMobile) {
      // goPrev/goNext read totalImages + photoIndex from the callback
      // params (not component state), so there is no stale-closure risk
      // even though buildCaption is memoized — fresh values flow in on
      // every call from the lightbox.
      const canNavigate = totalImages > 1;
      const goPrev = () =>
        setPhotoIndex((photoIndex + totalImages - 1) % totalImages);
      const goNext = () => setPhotoIndex((photoIndex + 1) % totalImages);

      return (
        <div className={styles.galleryCaption}>
          {canNavigate && (
            <button
              type="button"
              className={`${styles.galleryCaptionArrow} ${styles.galleryCaptionArrowPrev}`}
              onClick={goPrev}
              aria-label="Prethodna slika"
            >
              <Icon type="left" />
            </button>
          )}
          <div className={styles.galleryCaptionContent}>
            {galleryInfo && <div className={styles.galleryCaptionDetails}>{galleryInfo}</div>}
            {renderLightboxNavigationIcons()}
            <span className={styles.galleryCaptionCounter}>{counter}</span>
          </div>
          {canNavigate && (
            <button
              type="button"
              className={`${styles.galleryCaptionArrow} ${styles.galleryCaptionArrowNext}`}
              onClick={goNext}
              aria-label="Sljedeća slika"
            >
              <Icon type="right" />
            </button>
          )}
        </div>
      );
    }

    // Desktop view - counter, icons, and info in a row
    return (
      <div className={styles.galleryCaptionDesktop}>
        <span className={styles.galleryCaptionCounter}>{counter}</span>
        {renderLightboxNavigationIcons()}
        {galleryInfo && <span className={styles.galleryCaptionDetails}>{galleryInfo}</span>}
      </div>
    );
  }, [getGalleryInfo, isMobile, renderLightboxNavigationIcons]);

  // Handle Android back button to close gallery
  useEffect(() => {
    if (!isOpen) return;

    // Push a state when gallery opens
    window.history.pushState({ gallery: true }, '');

    const handlePopState = () => {
      setIsOpen(false);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen]);

  useEffect(() => {
    // Add regular images
    const tempImagesSrc = images.map((imageName: string) => ({
      src: getImageLink(imageName, isMobile ? ImageSizeEnum.adMedium : ImageSizeEnum.adLarge),
      alt: "Slike oglasa",
    }));

    // Add floor plan images
    floorPlans.forEach((floorPlanName: string) => {
      tempImagesSrc.push({
        src: getImageLink(floorPlanName, isMobile ? ImageSizeEnum.adMedium : ImageSizeEnum.adLarge),
        alt: FloorPlanAlt,
      });
    });

    if (!videoUrl) {
      setImagesSrc(tempImagesSrc);
    } else {
      parseVideoEmbedSrc(videoUrl).then(videoEmbedSrc => {
        if (videoEmbedSrc) {
          setVideoWrapper(renderVideoEmbed(videoEmbedSrc));
          tempImagesSrc.push({ src: videoEmbedSrc.thumbnail, alt: VideoThumbnailAlt });
          setImagesSrc(tempImagesSrc);
        }
      });
    }
  }, [images, floorPlans, videoUrl]);

  const onClick = (e, index) => {
    e.preventDefault();

    setIsOpen(true);
    setPhotoIndex(index);
  };




  const handleLightboxRef = ref => {
    if (!ref) {
      return;
    }

    const component = ref;
    component.componentDidUpdate = lightboxDidUpdate;
  };

  const lightboxDidUpdate = () => {
    const lightboxImage = window.document.getElementsByClassName(
      LightboxElementClass
    )[0];

    const lightboxImageVideo = window.document.getElementById(
      VideoWrapperId
    );

    if (!lightboxImage || !videoWrapper) {
      return;
    }

    const imageAlt = imagesSrc[photoIndex].alt;
    if (imageAlt === VideoThumbnailAlt) {
      // @ts-ignore
      lightboxImage.hidden = true;
      if (!lightboxImageVideo) {
        lightboxImage.parentNode.appendChild(videoWrapper);
      } else {
        lightboxImageVideo.style.display = "block";
      }
    } else {
      // @ts-ignore
      lightboxImage.hidden = false;

      if (lightboxImageVideo) {
        lightboxImageVideo.style.display = "none";
        lightboxImageVideo.childNodes.forEach(function (e) {
          let service = e.getAttribute('embedService');
          if (VideoService.vimeo === service) {
            e.contentWindow.postMessage('{"method":"pause"}', '*')
          }

          if (VideoService.youtube === service) {
            e.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*')
          }
        });
      }
    }
  };

  const renderVideoEmbed = useCallback((videoEmbedSrc: videoEmbedSrc) => {
    if (!videoEmbedSrc) {
      return null;
    }

    const wrapper = document.createElement("div");
    const iframe = window.document.createElement("iframe");
    iframe.frameBorder = "0";
    iframe.className = styles.video;
    iframe.allowFullscreen = true;
    iframe.setAttribute("embedService", videoEmbedSrc.service);

    iframe.src = videoEmbedSrc.url;
    wrapper.id = VideoWrapperId;
    wrapper.className = styles.videoWrapper;
    wrapper.appendChild(iframe);
    return wrapper;
  }, []);

  const renderImage = useCallback(({ src, alt }, className) => (
    <React.Fragment>
      {alt === VideoThumbnailAlt ? (
        //TODO: Davor - maybe replace with unified video icon, or make handle for video service
        <svg className={styles.playIcon} viewBox="0 0 68 48">
          <path
            d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z"
            fill="#f00"
            fillOpacity="0.8"
          />
          <path d="M 45,24 27,14 27,34" fill="#fff" />
        </svg>
      ) : null}

      <div
        className={className}
        // @ts-ignore
        alt={alt}
        style={{ backgroundImage: `url(${src})` }}
      />
    </React.Fragment>
  ), []);




  const imageUrls = imagesSrc ? imagesSrc.map(i => i.src) : [];

  // Handler to navigate to a specific index in the lightbox
  const navigateToIndex = (e, index) => {
    e.preventDefault();
    if (index >= 0 && index < imagesSrc.length) {
      setIsOpen(true);
      setPhotoIndex(index);
    }
  };

  // Check if we should show navigation icons (show if floor plans OR video exists)
  const showNavigationIcons = galleryIndexes.hasFloorPlans || galleryIndexes.hasVideo;

  // Render gallery navigation icons
  const renderNavigationIcons = () => {
    if (!showNavigationIcons) return null;

    return (
      <div className={styles.galleryNavigation}>
        {galleryIndexes.hasFloorPlans && (
          <button
            type="button"
            className={styles.galleryNavButton}
            onTouchEnd={(e) => {
              e.stopPropagation();
              e.preventDefault();
              ReactGA.event({
                category: "Detalji oglasa",
                action: "Click",
                label: "Tlocrti"
              });
              navigateToIndex(e, galleryIndexes.firstFloorPlanIndex);
            }}
            onClick={(e) => {
              e.stopPropagation();
              ReactGA.event({
                category: "Detalji oglasa",
                action: "Click",
                label: "Tlocrti"
              });
              navigateToIndex(e, galleryIndexes.firstFloorPlanIndex);
            }}
            title={"Tlocrti"}
          >
            <img src={GALLERY_FLOORPLAN_ICON} alt="Tlocrti" />
          </button>
        )}
        {galleryIndexes.hasVideo && (
          <button
            type="button"
            className={styles.galleryNavButton}
            onTouchEnd={(e) => {
              e.stopPropagation();
              e.preventDefault();
              ReactGA.event({
                category: "Detalji oglasa",
                action: "Click",
                label: "Video"
              });
              navigateToIndex(e, galleryIndexes.videoIndex);
            }}
            onClick={(e) => {
              e.stopPropagation();
              ReactGA.event({
                category: "Detalji oglasa",
                action: "Click",
                label: "Video"
              });
              navigateToIndex(e, galleryIndexes.videoIndex);
            }}
            title={"Video"}
          >
            <img src={GALLERY_VIDEO_ICON} alt="Video" />
          </button>
        )}
      </div>
    );
  };

  return imageUrls.length > 0 ? (
    <React.Fragment>
      {isMobile ? (
        <div className={styles.carouselWrapper}>
          <IndexCarousel
            images={images}
            floorPlans={floorPlans}
            className={styles.carouselImage}
            videoUrl={videoUrl}
            onClick={onClick}
            showCounter
            eventLabel="single"
            navigationIcons={renderNavigationIcons()}
            showFullscreenIcon
          />
          {renderBadges()}
        </div>
      ) : (
        <Row className={styles.carouselWrapper}>
          {renderBadges()}
          {renderNavigationIcons()}
          <Col xs={24}>
            {imagesSrc.length === 1 ? (
              <React.Fragment>
                <span
                  onClick={e => onClick(e, 0)}
                  role="Button"
                  tabIndex={0}
                  className={styles.imageLink}
                >
                  {renderImage(
                    imagesSrc[0],
                    `${styles.carouselImage} ${styles.defaultImage}`
                  )}
                </span>
              </React.Fragment>
            ) : (
              imagesSrc.slice(0, 4).map((image, index) => (
                <Col
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  xs={24}
                  md={index === 0 ? 18 : 6}
                  className={
                    index === 0 ? styles.largeImage : styles.smallImage
                  }
                >
                  <span
                    onClick={e => onClick(e, index)}
                    role="Button"
                    tabIndex={0}
                    className={styles.imageLink}
                  >
                    {renderImage(image, styles.carouselImage)}
                    {index !== 3 || imagesSrc.length - 4 <= 0 ? null : (
                      <span className={styles.imageCounter}>
                        <span>+{imagesSrc.length - 4}</span>
                      </span>
                    )}
                  </span>
                </Col>
              ))
            )}
          </Col>
        </Row>
      )}
      {isOpen && (
        <React.Fragment>
          <Lightbox
            ref={handleLightboxRef}
            animationOnKeyInput
            imagePadding={isMedium ? 0 : 50}
            imageCaption={buildCaption(photoIndex, imagesSrc.length)}
            mainSrc={imagesSrc[photoIndex].src}
            nextSrc={imagesSrc[(photoIndex + 1) % imagesSrc.length].src}
            prevSrc={imagesSrc[(photoIndex + imagesSrc.length - 1) % imagesSrc.length].src}
            onCloseRequest={() => {
              // Go back in history to remove the pushed state
              window.history.back();
            }}
            onMovePrevRequest={() =>
              setPhotoIndex((photoIndex + imageUrls.length - 1) % imageUrls.length)
            }
            onMoveNextRequest={() =>
              setPhotoIndex((photoIndex + 1) % imageUrls.length)
            }
            wrapperClassName={`${styles.lightbox} ${styles.lightboxWithInfo}`}
          />


        </React.Fragment>
      )}
    </React.Fragment>
  ) : (
    <div
      className={`${styles.carouselImage} ${styles.defaultImage}`}
      style={getBackgroundImageSrc()}
    >
      {renderBadges()}
    </div>
  );
};

export default getWidthContext(IndexGallery);



// WEBPACK FOOTER //
// ./src/components/IndexGallery/IndexGallery.tsx